// Motor de diagnóstico del constructor de bots.
//
// Es una capa PURA (sin React, sin efectos): recibe los nodos, las reglas y los
// tokens del bot, y devuelve el resultado de evaluar cada regla contra cada nodo
// aplicable. El panel de UI solo consume `runDiagnostics`; así el motor queda
// aislado, testeable y reutilizable entre bots.
//
// Una regla describe QUÉ revisar y DÓNDE:
//   {
//     id, name, enabled, severity: 'error' | 'warn',
//     nodeType: '*' | 'webHook' | 'queryApi' | ...,   // en qué nodos aplica
//     field: 'url' | 'body' | 'variables.company.id_company',  // path en node.data
//     extractor: 'raw' | 'urlSegment',                // cómo obtener el valor
//     segAfter, segIndex,                             // solo si extractor === 'urlSegment'
//     operator: 'equals' | 'notEmpty' | 'contains' | 'regex',
//     expected,                                       // literal o token {{botId}}
//   }

// Tokens dinámicos disponibles. Se resuelven en tiempo de ejecución con los datos
// del bot actual, de modo que la MISMA regla sirve para todos los bots clonados.
export const DIAGNOSTIC_TOKENS = [
  { token: '{{botId}}', label: 'ID del bot (_id)' },
  { token: '{{companyId}}', label: 'ID de la compañía (company)' },
];

// Catálogo de operadores, para alimentar el editor de reglas.
export const OPERATORS = [
  { value: 'equals', label: 'Es igual a' },
  { value: 'notEmpty', label: 'Tiene valor (no vacío)' },
  { value: 'contains', label: 'Contiene' },
  { value: 'regex', label: 'Coincide con (regex)' },
];

export const EXTRACTORS = [
  { value: 'raw', label: 'Valor del campo tal cual' },
  { value: 'urlSegment', label: 'Segmento de una URL' },
];

// Reemplaza los tokens {{...}} de un texto con los valores del bot.
export const resolveExpected = (expected, tokens = {}) => {
  if (expected == null) return '';
  return String(expected).replace(/\{\{(\w+)\}\}/g, (match, key) =>
    tokens[key] != null ? String(tokens[key]) : match
  );
};

// Intenta parsear un string JSON; si no es JSON válido lo devuelve tal cual.
const maybeParseJSON = (value) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch (_) {
    return value;
  }
};

// Resuelve un path con puntos sobre node.data. Si en el camino encuentra un
// string que en realidad es JSON (p. ej. `body` o `variables` de queryApi), lo
// parsea para poder seguir navegando dentro.
export const getFieldValue = (node, field) => {
  if (!node || !field) return undefined;
  const segments = String(field).split('.');
  let current = node.data;
  for (let i = 0; i < segments.length; i += 1) {
    if (current == null) return undefined;
    current = maybeParseJSON(current);
    if (typeof current !== 'object') return undefined;
    current = current[segments[i]];
  }
  return current;
};

// A partir de una URL, extrae el segmento indicado. `segAfter` marca desde dónde
// contar (ej. "webhook/"); `segIndex` es el índice del tramo tras ese punto.
// Si no hay `segAfter`, indexa sobre todos los segmentos del path.
export const extractUrlSegment = (rawUrl, segAfter = '', segIndex = 0) => {
  if (rawUrl == null) return undefined;
  let str = String(rawUrl);

  // Recorta query string y fragmento; no forman parte del path.
  str = str.split('?')[0].split('#')[0];

  let tail = str;
  if (segAfter) {
    const idx = str.indexOf(segAfter);
    if (idx === -1) return undefined; // el ancla no aparece: no aplica
    tail = str.slice(idx + segAfter.length);
  }

  const parts = tail.split('/').filter((p) => p !== '');
  const index = Number(segIndex) || 0;
  return parts[index];
};

// Obtiene el valor final a evaluar según el extractor de la regla.
const getEvaluableValue = (node, rule) => {
  const rawValue = getFieldValue(node, rule.field);
  if (rule.extractor === 'urlSegment') {
    return extractUrlSegment(rawValue, rule.segAfter, rule.segIndex);
  }
  return rawValue;
};

// Aplica el operador. Devuelve { ok, reason } para poder explicar el fallo.
const applyOperator = (actual, rule, tokens) => {
  const actualStr = actual == null ? '' : String(actual);
  const expected = resolveExpected(rule.expected, tokens);

  switch (rule.operator) {
    case 'notEmpty':
      return {
        ok: actualStr.trim().length > 0,
        reason: actualStr.trim().length > 0 ? '' : 'El campo está vacío.',
      };
    case 'equals':
      return {
        ok: actualStr === expected,
        reason:
          actualStr === expected
            ? ''
            : `Se esperaba "${expected}" y se encontró "${actualStr || '(vacío)'}".`,
      };
    case 'contains':
      return {
        ok: actualStr.includes(expected),
        reason: actualStr.includes(expected)
          ? ''
          : `No contiene "${expected}".`,
      };
    case 'regex': {
      try {
        const re = new RegExp(expected);
        const ok = re.test(actualStr);
        return { ok, reason: ok ? '' : `No coincide con /${expected}/.` };
      } catch (_) {
        return { ok: false, reason: 'La expresión regular no es válida.' };
      }
    }
    default:
      return { ok: false, reason: 'Operador no reconocido.' };
  }
};

// Indica si una regla aplica a un nodo dado (por tipo).
const ruleAppliesToNode = (rule, node) =>
  rule.nodeType === '*' || rule.nodeType === node.type;

// Evalúa una regla contra un nodo concreto.
export const evaluateRuleOnNode = (rule, node, tokens) => {
  const actual = getEvaluableValue(node, rule);
  const { ok, reason } = applyOperator(actual, rule, tokens);
  return {
    node,
    ok,
    actual: actual == null ? '' : String(actual),
    reason,
  };
};

// Ejecuta TODAS las reglas activas sobre TODOS los nodos aplicables.
// Devuelve un arreglo con el detalle por regla.
export const runDiagnostics = (nodes = [], rules = [], tokens = {}) => {
  const activeRules = rules.filter((r) => r && r.enabled !== false);

  return activeRules.map((rule) => {
    const applicableNodes = nodes.filter((n) => ruleAppliesToNode(rule, n));
    const results = applicableNodes.map((node) =>
      evaluateRuleOnNode(rule, node, tokens)
    );
    const failures = results.filter((r) => !r.ok);

    return {
      rule,
      results,
      failures,
      applicableCount: applicableNodes.length,
      passCount: results.length - failures.length,
      failCount: failures.length,
      // Una regla que no aplicó a ningún nodo se reporta aparte (ni pasa ni falla).
      status:
        applicableNodes.length === 0
          ? 'skipped'
          : failures.length === 0
          ? 'pass'
          : 'fail',
    };
  });
};

// Genera un id estable y único para una regla nueva.
export const newRuleId = () =>
  `rule_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Plantilla base para "Nueva regla" en el editor.
export const blankRule = () => ({
  id: newRuleId(),
  name: '',
  enabled: true,
  severity: 'error',
  nodeType: '*',
  field: 'url',
  extractor: 'raw',
  segAfter: '',
  segIndex: 0,
  operator: 'equals',
  expected: '',
});

// Plantillas listas basadas en las validaciones recurrentes al clonar un bot.
export const RULE_TEMPLATES = [
  {
    label: 'WebHook apunta a este bot',
    build: () => ({
      ...blankRule(),
      name: 'WebHook apunta a este bot',
      nodeType: 'webHook',
      field: 'url',
      extractor: 'urlSegment',
      segAfter: 'chatBotWebhooks/',
      segIndex: 0,
      operator: 'equals',
      expected: '{{botId}}',
      severity: 'error',
    }),
  },
  {
    label: 'QueryApi usa el companyId correcto',
    build: () => ({
      ...blankRule(),
      name: 'QueryApi usa el companyId correcto',
      nodeType: 'queryApi',
      field: 'url',
      extractor: 'urlSegment',
      segAfter: 'webhook/',
      segIndex: 0,
      operator: 'equals',
      expected: '{{companyId}}',
      severity: 'error',
    }),
  },
];
